import React from 'react';
import { Link } from 'react-router-dom';
import { PALETTE } from '../pages/site/SiteLayout';
import {
  FileText, LayoutGrid, PackageCheck, Truck, Landmark, ArrowRight,
} from 'lucide-react';

/**
 * HowItWorks, the "office to invoice" workflow in five steps.
 *
 * Two render modes:
 *   compact  → used on the marketing home page (fits between hero and features)
 *   full     → used on the dedicated /site/how-it-works page (adds detail rows)
 *
 * Key differentiator surfaced below the flow:
 *   Mode-aware invoicing. Fixed-price retail SKUs invoice at PO quantity
 *   within tolerance (off-PO invoices trigger retailer short-pays); per-kg
 *   trade invoices at the actual catch weight dispatched. The operations
 *   layer records real weights and batches in both modes.
 */
const STEPS = [
  {
    icon: FileText,
    n: '01',
    title: 'PO arrives',
    line: 'Staff open the Sales Order Entry page and type the PO in. Customer, delivery date, lines.',
    detail: 'Customer SKU codes autocomplete against their catalogue, Keystone remembers every customer\'s specific SKU ↔ your product ↔ your brand packaging mapping. Enter the retailer\'s code, the internal product and pack format resolve automatically.',
  },
  {
    icon: LayoutGrid,
    n: '02',
    title: 'Weekly pipeline',
    line: 'Every open order lands in the same window, grouped by production week.',
    detail: 'One view. Every order. Grouped by production make-day so ops can see what has to leave the door on Tuesday, Wednesday, Thursday at a glance. No juggling three spreadsheets.',
  },
  {
    icon: PackageCheck,
    n: '03',
    title: 'Actual production',
    line: 'Ops fill in the real batch codes, weights, units and cases against each order.',
    detail: 'This is where reality lives. A 100kg PO becomes 108kg actual because cheese cuts don\'t hit exact numbers. A 24-case order might ship as 23 + 1 short. Keystone captures every gram, every unit, every batch, this is your traceability spine.',
  },
  {
    icon: Truck,
    n: '04',
    title: 'Dispatch',
    line: 'One click generates the retailer-specific dispatch note, GS1-128 pallet label and COA.',
    detail: 'Tesco format for Tesco. Aldi format for Aldi. SuperValu format for SuperValu. Musgrave, Rewe, Biedronka, Auchan, every customer\'s template lives inside Keystone. No copying into Word. No format-mismatch depot rejections.',
  },
  {
    icon: Landmark,
    n: '05',
    title: 'Invoice to accounts',
    line: 'Pushed to AccountsIQ / Xero / Sage / QuickBooks via API, at the quantity each customer trades on.',
    detail: 'Fixed-price retail SKUs invoice at the PO quantity within agreed tolerance, an off-PO invoice triggers short-pays and query queues at the retailer\'s AP. Per-kg customers invoice at the actual catch weight dispatched. Keystone applies each customer\'s mode automatically; on PO-priced lines the weight variance stays recorded and reportable in the operations layer.',
  },
];

const CalloutCard = ({ children }) => (
  <div style={{
    marginTop: 36,
    padding: '26px 30px',
    background: PALETTE.navy,
    color: '#fff',
    borderRadius: 8,
    borderLeft: `4px solid ${PALETTE.gold}`,
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    gap: 20,
    alignItems: 'center',
  }} className="hiw-callout">
    {children}
  </div>
);

const Step = ({ step, i, compact, showConnector }) => {
  const Icon = step.icon;
  return (
    <>
      <div
        data-testid={`hiw-step-${i + 1}`}
        style={{
          background: '#fff',
          border: `1px solid ${PALETTE.border}`,
          borderTop: `3px solid ${PALETTE.magenta}`,
          borderRadius: 8,
          padding: compact ? '22px 22px 20px' : '30px 28px 26px',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
          position: 'relative',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <div style={{
            width: 40, height: 40, borderRadius: 8,
            background: '#FDE7F0',
            color: PALETTE.magenta,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon size={20} strokeWidth={2} />
          </div>
          <div style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 26,
            fontWeight: 700,
            color: PALETTE.border,
            lineHeight: 1,
            letterSpacing: '-0.02em',
          }}>{step.n}</div>
        </div>

        <div>
          <div style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: compact ? 20 : 24,
            fontWeight: 700,
            color: PALETTE.navy,
            letterSpacing: '-0.01em',
            marginBottom: 8,
          }}>{step.title}</div>
          <div style={{ fontSize: 13.5, lineHeight: 1.55, color: PALETTE.slate }}>{step.line}</div>
          {!compact && (
            <div style={{
              marginTop: 14, paddingTop: 14, borderTop: `1px solid ${PALETTE.borderSoft || '#EEF0F4'}`,
              fontSize: 13.5, lineHeight: 1.6, color: PALETTE.slate,
            }}>{step.detail}</div>
          )}
        </div>
      </div>
      {showConnector && (
        <div
          aria-hidden="true"
          className="hiw-connector"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: PALETTE.magenta,
          }}
        >
          <ArrowRight size={22} strokeWidth={2.4} />
        </div>
      )}
    </>
  );
};

export default function HowItWorks({ compact = false, showCta = true }) {
  return (
    <section
      data-testid="how-it-works"
      style={{
        background: PALETTE.cream,
        padding: compact ? '80px 32px' : '120px 32px',
        borderTop: `1px solid ${PALETTE.border}`,
        borderBottom: `1px solid ${PALETTE.border}`,
      }}
    >
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{
          textAlign: 'center', maxWidth: 780, margin: '0 auto 48px',
        }}>
          <div style={{
            fontSize: 11, letterSpacing: '0.24em', textTransform: 'uppercase',
            fontWeight: 700, color: PALETTE.magenta, marginBottom: 14,
          }}>
            How it works, from PO to invoice
          </div>
          <h2 style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 'clamp(30px, 4vw, 48px)',
            fontWeight: 700,
            color: PALETTE.navy,
            margin: '0 0 18px',
            letterSpacing: '-0.02em',
            lineHeight: 1.05,
          }}>
            Five steps.{' '}
            <em style={{ fontStyle: 'italic', fontWeight: 500, color: PALETTE.magenta }}>
              Monday morning to Friday invoice.
            </em>
          </h2>
          <p style={{ fontSize: 16, lineHeight: 1.6, color: PALETTE.slate, margin: 0 }}>
            The daily rhythm of a food manufacturer, PO in the door, produced on the floor, dispatched to the retailer, invoiced through your accounts. Keystone is the layer that connects those five moments without spreadsheets in between.
          </p>
        </div>

        {/* Flow grid, 5 cards + 4 connector arrows */}
        <div
          className={compact ? 'hiw-grid-compact' : 'hiw-grid-full'}
          style={{
            display: 'grid',
            gridTemplateColumns: compact
              ? 'repeat(5, 1fr) 0'                       // 5 equal cards on desktop
              : '1fr 30px 1fr 30px 1fr 30px 1fr 30px 1fr', // with connectors on full page
            columnGap: compact ? 14 : 0,
            rowGap: 16,
            alignItems: 'stretch',
          }}
        >
          {STEPS.map((s, i) => (
            <Step
              key={s.n}
              step={s}
              i={i}
              compact={compact}
              showConnector={!compact && i < STEPS.length - 1}
            />
          ))}
        </div>

        {/* The kill-shot: PO-level invoice / actual-level dispatch */}
        <CalloutCard>
          <div>
            <div style={{
              fontSize: 11, letterSpacing: '0.20em', textTransform: 'uppercase',
              fontWeight: 700, color: PALETTE.gold, marginBottom: 10,
            }}>
              Why this matters
            </div>
            <div style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: 'clamp(22px, 2.4vw, 30px)',
              fontWeight: 700, color: '#fff',
              letterSpacing: '-0.01em', lineHeight: 1.2, marginBottom: 10,
            }}>
              You dispatched 108kg. What should the invoice say?
            </div>
            <div style={{ fontSize: 14.5, lineHeight: 1.6, color: '#CBD5E1', maxWidth: 640 }}>
              Depends on the deal. Fixed-price retail SKU: the <strong style={{ color: '#fff', fontWeight: 700 }}>100kg PO quantity</strong>, within tolerance, so the retailer's AP matches and pays without a query. Per-kg trade: the <strong style={{ color: '#fff', fontWeight: 700 }}>actual 108kg catch weight</strong>. Keystone knows each customer's mode and raises the right invoice, and the operations layer keeps the real weights and batches either way.
            </div>
          </div>
          {showCta && (
            <Link
              to="/site/pricing"
              data-testid="hiw-cta"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '14px 22px',
                background: PALETTE.magenta,
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                textDecoration: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              See pricing <ArrowRight size={16} />
            </Link>
          )}
        </CalloutCard>
      </div>

      <style>{`
        /* Compact home-page grid: 5 columns on desktop, 2 then 1 on smaller screens */
        @media (max-width: 1150px) {
          .hiw-grid-compact { grid-template-columns: repeat(3, 1fr) !important; }
          .hiw-grid-full    { grid-template-columns: 1fr !important; }
          .hiw-grid-full .hiw-connector { display: none !important; }
        }
        @media (max-width: 780px) {
          .hiw-grid-compact { grid-template-columns: repeat(2, 1fr) !important; }
        }
        @media (max-width: 520px) {
          .hiw-grid-compact,
          .hiw-grid-full { grid-template-columns: 1fr !important; }
          .hiw-callout { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}
